import React, { forwardRef } from 'react';
import { createMemoStyles, withNested, conditionGuard, safeRest } from '@wds/utils';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles$6 = createMemoStyles(function (_a) {
  var spacing = _a.spacing,
    radius = _a.radius,
    palette = _a.palette,
    type = _a.type;
  var card = {
    padding: spacing[5],
    bgColor: {
      light: palette.background.light,
      main: palette.background.main
    }
  };
  return {
    wrenchCardContainer: function () {
      return {
        maxWidth: '368px',
        position: 'relative'
      };
    },
    wrenchCardAddon: function () {
      return {
        position: 'absolute',
        top: 100
      };
    },
    wrenchCard: function (_a) {
      var interactive = _a.interactive,
        backgroundColor = _a.backgroundColor;
      return __assign({
        width: '100%',
        height: '100%',
        color: palette.text.dark,
        display: 'inline-flex',
        alignItems: 'stretch',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: [spacing[5]],
        cursor: interactive ? 'pointer' : null,
        backgroundColor: card.bgColor[backgroundColor],
        borderRadius: radius.medium,
        border: '2px solid transparent',
        boxShadow: '0 0 0 2px #EDEEEE',
        fontFamily: type.fontFamily.base,
        fontSize: type.fontSize.body,
        '-webkit-font-smoothing': 'antialiased',
        textDecoration: 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxSizing: 'border-box'
      }, interactive && {
        '&:hover': {
          borderColor: 'transparent',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.14)'
        },
        '&:focus': {
          borderColor: '#d0d2d3',
          boxShadow: 'none'
        },
        '&:active': {
          borderColor: '#d0d2d3',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.14)'
        }
      });
    }
  };
});
var useStyles$5 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchCardGrid: {
      display: 'grid',
      gap: spacing[5],
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))'
    }
  };
});
var CardGrid = forwardRef(function (_a, ref) {
  var children = _a.children;
  var wrenchCardGrid = useStyles$5().wrenchCardGrid;
  return React.createElement("div", {
    className: wrenchCardGrid,
    ref: ref
  }, children);
});
CardGrid.displayName = 'CardGrid';
var useStyles$4 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchCardPreview: function (_a) {
      var padding = _a.padding;
      return {
        margin: padding ? "0 0 0 0" : "-".concat(spacing[5], "px -").concat(spacing[5], "px 0 -").concat(spacing[5], "px")
      };
    }
  };
});
var CardPreview = function (_a) {
  var children = _a.children,
    _b = _a.padding,
    padding = _b === void 0 ? true : _b;
  var wrenchCardPreview = useStyles$4({
    padding: padding
  }).wrenchCardPreview;
  return React.createElement("div", {
    className: wrenchCardPreview
  }, children);
};
CardPreview.displayName = 'CardPreview';
var useStyles$3 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchCardToolbar: {
      marginBottom: spacing[5],
      minWidth: '192px',
      width: '100%',
      textAlign: 'left'
    }
  };
});
var CardToolbar = function (_a) {
  var children = _a.children;
  var wrenchCardToolbar = useStyles$3().wrenchCardToolbar;
  return React.createElement("div", {
    className: wrenchCardToolbar
  }, children);
};
CardToolbar.displayName = 'CardToolbar';
var useStyles$2 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchCardHeader: {
      marginBottom: spacing[5],
      minWidth: '192px'
    }
  };
});
var CardHeader = function (_a) {
  var children = _a.children;
  var wrenchCardHeader = useStyles$2().wrenchCardHeader;
  return React.createElement("header", {
    className: wrenchCardHeader
  }, children);
};
CardHeader.displayName = 'CardHeader';
function getLineTruncationProperties(truncate) {
  if (truncate !== undefined && truncate > 0) {
    return {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whitespace: truncate === 1 ? 'nowrap' : undefined,
      display: '-webkit-box',
      boxOrient: 'vertical',
      lineClamp: truncate
    };
  }
  return undefined;
}
var useStyles$1 = createMemoStyles(function (theme) {
  return {
    wrenchCardBody: function (_a) {
      var verticalAlign = _a.verticalAlign,
        truncateNumberOfLines = _a.truncateNumberOfLines;
      return __assign({
        minWidth: '192px',
        marginLeft: '0',
        marginRight: '0',
        marginTop: verticalAlign === 'top' ? '0' : 'auto',
        marginBottom: verticalAlign === 'bottom' ? '0' : 'auto'
      }, getLineTruncationProperties(truncateNumberOfLines));
    }
  };
});
var CardBody = function (_a) {
  var children = _a.children,
    _b = _a.verticalAlign,
    verticalAlign = _b === void 0 ? 'center' : _b,
    truncateNumberOfLines = _a.truncateNumberOfLines;
  var wrenchCardBody = useStyles$1({
    verticalAlign: verticalAlign,
    truncateNumberOfLines: truncateNumberOfLines
  }).wrenchCardBody;
  return React.createElement("div", {
    className: wrenchCardBody
  }, children);
};
CardBody.displayName = 'CardBody';
var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchCardFooter: {
      marginTop: spacing[5],
      minWidth: '192px'
    }
  };
});
var CardFooter = function (_a) {
  var children = _a.children;
  var wrenchCardFooter = useStyles().wrenchCardFooter;
  return React.createElement("footer", {
    className: wrenchCardFooter
  }, children);
};
CardFooter.displayName = 'CardFooter';
var Card = function (_a) {
  var children = _a.children,
    interactiveAddon = _a.interactiveAddon,
    href = _a.href,
    _b = _a.backgroundColor,
    backgroundColor = _b === void 0 ? 'light' : _b,
    rest = __rest(_a, ["children", "interactiveAddon", "href", "backgroundColor"]);
  // cast to boolean if href or onClick is defined
  var interactive = !!(href !== null && href !== void 0 ? href : rest.onClick);
  var _c = useStyles$6({
      interactive: interactive,
      backgroundColor: backgroundColor
    }),
    wrenchCard = _c.wrenchCard,
    wrenchCardContainer = _c.wrenchCardContainer;
  var content;
  // href means it is an anchor element
  if (conditionGuard(rest, !!href)) {
    content = React.createElement("a", __assign({
      href: href,
      className: wrenchCard
    }, safeRest(rest)), children);
  } else if (conditionGuard(rest, !!rest.onClick)) {
    // otherwise, if it has onClick, it will be a button
    content = React.createElement("button", __assign({
      className: wrenchCard,
      type: "button"
    }, safeRest(rest)), children);
  } else {
    // non-interactive
    content = React.createElement("div", __assign({
      className: wrenchCard
    }, safeRest(rest)), children);
  }
  return React.createElement("div", {
    className: wrenchCardContainer
  }, content, interactiveAddon);
};
Card.displayName = 'Card';
var CardWithNested = withNested(Card, {
  Grid: CardGrid,
  Header: CardHeader,
  Preview: CardPreview,
  Toolbar: CardToolbar,
  Body: CardBody,
  Footer: CardFooter
});

// following example set by ChipSize in constants.ts for Chip component
// this will allow us to create more background color options easily in the future
var CARD_BACKGROUND_COLOR = ['main', 'light'];
export { CARD_BACKGROUND_COLOR, CardWithNested as Card };